import type { JSX, ReactNode } from 'react'

import { Skeleton, type SkeletonShape } from '@lensmira/ui'

import { useTranslation } from '../i18n.js'

/**
 * The six states every data surface has to have. `DESIGN-TOKENS.md` §8 `ST-02`.
 *
 * > "Every data surface is specified in: loading (skeleton, not a spinner), empty (first-run vs
 * > filtered-to-nothing, **different copy**), partial, error with retry, and permission-denied."
 *
 * **One component, because the alternative is twenty-six screens each getting four of the six.**
 * That is what the counter had: a line of small grey text for loading, one sentence doing duty for
 * both empty cases, and nothing at all for partial, error or permission-denied. A surface that
 * cannot say "you are not allowed to see this" says "there is nothing here", which is a different
 * and false statement.
 *
 * **First-run and filtered-to-nothing are different copy because they are different situations.**
 * "No customer is on file yet" is an invitation to add one. "No customer matches that" is a
 * suggestion to search for something else. One sentence serving both is wrong half the time, and
 * on the first day of a new shop it is wrong in the discouraging direction.
 *
 * **Error carries a retry.** A dead surface with a sentence on it is a screen somebody reloads the
 * whole app to escape, which on a counter tablet loses whatever was in hand.
 *
 * The caller decides which state it is in, because only the caller knows: the read's shape, the
 * filter it applied, and the permission it holds are all above this.
 */

export type SurfaceState =
  | { readonly kind: 'ready' }
  | { readonly kind: 'loading' }
  /** The shop has none of these yet. */
  | { readonly kind: 'empty' }
  /** There are some, and none match what was asked for. */
  | { readonly kind: 'filtered' }
  /** Some of it arrived and some did not. The partial case `ST-02` names. */
  | { readonly kind: 'partial'; readonly missing: string }
  | { readonly kind: 'error'; readonly message: string; readonly onRetry?: () => void }
  /** Appendix B refused the read. The surface says so rather than showing nothing. */
  | { readonly kind: 'denied' }

export interface DataSurfaceProps {
  readonly state: SurfaceState
  /** The shape the skeleton takes, so it does not reflow when the content lands (`ST-03`). */
  readonly shape: SkeletonShape
  /** How many rows the skeleton draws. The usual size of this surface, not its maximum. */
  readonly rows?: number
  readonly columns?: number
  /** What this surface is, so the empty and denied sentences can name it. */
  readonly what: string
  /** First-run copy. The caller's, because only it knows what adding one means here. */
  readonly emptyMessage?: string
  readonly filteredMessage?: string
  /**
   * Denied copy, for a surface whose refusal has a remedy worth naming.
   *
   * `surface.denied` says "you are not allowed to see {what}", which is true and is where a reader
   * stops. `SupplierInvoice` kept its refusal outside this component as a bare `lm-hint` paragraph
   * for exactly that reason: `ROLE-001a` withholds this order's costs and with them its currency,
   * and a supplier invoice is a cost-price document, so the sentence that helps is the one that
   * says to ask the owner for cost visibility at this branch. Without the prop that paragraph was
   * outside the surface and so had no `role="status"` and none of the denied styling, which is the
   * `ST-02` state it is.
   */
  readonly deniedMessage?: string
  readonly children: ReactNode
}

export function DataSurface({
  state,
  shape,
  rows,
  columns,
  what,
  emptyMessage,
  filteredMessage,
  deniedMessage,
  children,
}: DataSurfaceProps): JSX.Element {
  const { t } = useTranslation()

  if (state.kind === 'loading') {
    return (
      <div className="lm-surface lm-surface--loading">
        {/*
          The announcement is here and not on the skeleton, which is `aria-hidden`: a screen reader
          handed a dozen empty boxes is worse off than one handed nothing, because the boxes are a
          drawing of where content will be rather than content.
        */}
        <p className="lm-visually-hidden" role="status">
          {t('surface.loading', { what })}
        </p>
        <Skeleton
          shape={shape}
          {...(rows === undefined ? {} : { count: rows })}
          {...(columns === undefined ? {} : { columns })}
        />
      </div>
    )
  }

  if (state.kind === 'denied') {
    return (
      <p className="lm-surface lm-surface--denied" role="status">
        {deniedMessage ?? t('surface.denied', { what })}
      </p>
    )
  }

  if (state.kind === 'error') {
    return (
      <div className="lm-surface lm-surface--error" role="alert">
        <p className="lm-surface__message">{state.message}</p>
        {state.onRetry === undefined ? null : (
          <button type="button" className="lm-button lm-button--secondary" onClick={state.onRetry}>
            {t('surface.retry')}
          </button>
        )}
      </div>
    )
  }

  if (state.kind === 'empty') {
    return (
      <p className="lm-empty">{emptyMessage ?? t('surface.empty', { what })}</p>
    )
  }

  if (state.kind === 'filtered') {
    return <p className="lm-empty">{filteredMessage ?? t('surface.filtered', { what })}</p>
  }

  return (
    <>
      {state.kind === 'partial' ? (
        // Shown above the content rather than instead of it: some of it arrived, and a shopkeeper
        // can use that while knowing what is missing.
        <p className="lm-surface lm-surface--partial" role="status">
          {t('surface.partial', { missing: state.missing })}
        </p>
      ) : null}
      {children}
    </>
  )
}
